import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Snackbar,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { collectionOrdersStyles } from "../utils/styles/collectionOrdersStyles";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import InfiniteScroll from "react-infinite-scroll-component";
import { ProOrganisation, ProInvitation, getProOrganisationsList, getInvites } from "../redux/actions/subscriptions";
import { HeaderWithButtons, Searchbar } from "../components-molecules";
import { CustomTabs } from "../components-molecules/Tabs";
import { CustomTab } from "../components-molecules/Tab";
import ProOrganisationCard from "../components-molecules/ProOrganisationCard";
import { useHistory, useLocation } from "react-router-dom";
import { ButtonPrimary } from "../components-atoms";
import { Add } from "@material-ui/icons";
import { buttonHoverAndActive } from "../utils/styles/buttonStyles";
import { makeStyles } from "@material-ui/core";
import InviteProTeamModal from "../components-molecules/InviteProTeamModal";
import { formatDate } from "../utils/dateTimeFormatting";

const TK_PRO_TEAMS_TABS = ["Current Teams", "Invitations"];
const pageSize = 10;

type UpdateType = "ProOrganisationsList";

export interface DataUpdateSocketMessage {
  LastUpdate: string;
  UpdateType: UpdateType;
}

export function TkProTeams() {
  const location = useLocation();
  const history = useHistory();

  const classes = collectionOrdersStyles();
  const buttonClasses = useButtonStyles();
  const [proOrganisations, setProOrganisations] = useState<ProOrganisation[]>([]);
  const [invitations, setInvitations] = useState<ProInvitation[]>([]);

  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const currentPageRef = useRef(0);

  // URL Filters
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = useMemo(() => Number(urlParams.get('page')), [urlParams]) || 0;
  const query = useMemo(() => urlParams.get('query'), [urlParams]) || '';
  const searchOpen = useMemo(() => urlParams.get('searchOpen') === 'true', [urlParams]) || false;
  const tabIndex = useMemo(() => Number(urlParams.get('tabIndex')), [urlParams]) || 0;

  const setParam = useCallback((key: string, value: any) => {
    urlParams.delete(key);
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          urlParams.append(key, v);
        });
      } else {
        urlParams.set(key, value);
      }
      history.push({ pathname: location.pathname, search: urlParams.toString() });
    }
  }, [urlParams, history, location.pathname]);

  const deleteParam = useCallback((key: string) => {
    urlParams.delete(key);
    history.push({ pathname: location.pathname, search: urlParams.toString() });
  }, [urlParams, history, location.pathname]);

  const setPage = useCallback((p: number) => {
    setParam('page', p);
  }, [setParam]);

  const getProOrganisations = useCallback((currentPage = 0) => {
    getProOrganisationsList({
      Page: currentPage,
      Size: pageSize,
      Query: query
    },
      (res) => {
        if (currentPage === 0) {
          setProOrganisations(res.Organisations);
        }
        else {
          setProOrganisations((prev) => ([
            ...prev,
            ...res.Organisations
          ]));
        }
        // If we get fewer results than pageSize, we've reached the end
        setHasMore(res.Organisations.length === pageSize);
        setLoading(false);
      }, (err) => {
        setLoading(false);
      });
  }, [query]);

  const getInvitations = useCallback(() => {
    getInvites(
      {
        Page: page,
        Size: pageSize,
        Query: query
      },
      (res) => {
        setInvitations(res.Invites || []);
        setLoading(false);
      },
      (err) => {
        setInvitations([]);
        setLoading(false);
      }
    );
  }, [page, query]);

  const copyToClipboard = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
    } catch (err) {
    }
  }, []);

  const refreshList = useCallback(() => {
    if (tabIndex === 0) {
      setProOrganisations([]); // Clear list on refresh
      currentPageRef.current = 0; // Reset page to 0
      setPage(0); // Reset page to 0
      setHasMore(true); // Reset hasMore
      getProOrganisations(0); // Fetch first page
    } else {
      getInvitations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProOrganisations, tabIndex]);

  useEffect(() => {
    refreshList();
  }, [query, refreshList]);

  const onTabChange = (_: number, newValue: number) => {
    if (newValue !== Number(tabIndex)) {
      setParam('tabIndex', newValue);
      setPage(0);
      setLoading(true);
      setProOrganisations([]);
      setInvitations([]);
    }
  };

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = currentPageRef.current + 1;
      getProOrganisations(nextPage);
      currentPageRef.current = nextPage;
      setPage(nextPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, getProOrganisations]);

  const renderTabs = () => {
    return (
      <Container className={classes.tabContainer}>
        <CustomTabs value={tabIndex} onChange={onTabChange}>
          {TK_PRO_TEAMS_TABS.map((tab: string) => (
            <CustomTab key={tab} label={tab} />
          ))}
        </CustomTabs>
      </Container>
    );
  };

  const renderHeader = () => {
    return (
      <HeaderWithButtons title="TK Pro Teams">
        <HeaderWithButtons.ButtonArea>
          {tabIndex === 1 && (
            <ButtonPrimary
              data-automation="invite-button"
              variant="contained"
              color="primary"
              disableElevation
              className={buttonClasses.primaryButton}
              startIcon={<Add />}
              onClick={() => setInviteModalOpen(true)}
            >
              Invite Team
            </ButtonPrimary>
          )}
          <IconButton
            data-automation="search-button"
            className={classes.searchIconButton}
            color="primary"
            onClick={() => searchOpen ? deleteParam('searchOpen') : setParam('searchOpen', true)}
          >
            <SearchRoundedIcon
              color="primary"
              className={classes.searchIcon}
            />
          </IconButton>
        </HeaderWithButtons.ButtonArea>
      </HeaderWithButtons>
    );
  };

  return (
    <Container className={classes.root}>
      {renderHeader()}
      {renderTabs()}
      {!loading &&
        <Searchbar
          query={query}
          isOpen={searchOpen}
          placeholder="Search TK Pro Teams"
          onChange={(e) => {
            setParam('query', e.target.value);
          }}
          onCancel={() => {
            deleteParam('query');
            deleteParam('searchOpen');
          }}
        />
      }
      <Box mt={3}>
        {query.length > 0 && ((tabIndex === 0 && proOrganisations.length === 0) || (tabIndex === 1 && invitations.length === 0)) ?
          <Grid container item className={classes.uPaddingLeft} style={{ marginTop: '40px' }}>
            <Grid container item style={{ maxWidth: '320px' }}>
              <Typography variant="h6" component="span">
                0 results found
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                component="span"
                style={{ marginTop: '12px' }}>
                Adjust your filtering and try again
              </Typography>
            </Grid>
          </Grid>
          : null}
        {loading && page === 0 ? (
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            style={{ marginTop: "96px" }}
          >
            <CircularProgress />
          </Grid>
        ) : tabIndex === 0 ? (
          proOrganisations.length > 0 ? (
            <InfiniteScroll
              style={{ overflow: "hidden" }}
              dataLength={proOrganisations.length}
              next={loadMore}
              scrollThreshold={0.8}
              hasMore={hasMore}
              loader={
                <Container style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Container>
              }>
              {proOrganisations?.map((o, i) => <ProOrganisationCard key={o.Id} refreshList={refreshList}{...o} />)}
            </InfiniteScroll>
          ) : (
            <Grid
              container
              item
              justifyContent="center"
              alignItems="center"
              style={{ marginTop: "96px" }}
            >
              <Grid
                container
                item
                justifyContent="center"
                alignItems="center"
                style={{ maxWidth: "320px" }}
              >
                <Typography variant="h6" component="span" align="center">
                  You don't have any TK Pro Teams
                </Typography>
              </Grid>
            </Grid>
          )
        ) : tabIndex === 1 ? (
          invitations.length > 0 ? (
            <Grid container spacing={2}>
              {invitations.map((invitation, i) => (
                <Grid item xs={12} key={i}>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      padding: "24px 16px 24px 24px",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      boxSizing: "border-box",
                      borderRadius: "20px",
                      transition: "all 200ms ease",
                      marginBottom: 16,
                    }}
                  >
                    <Box style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <Typography variant="h6" component="h2">
                          {invitation.FirstName} {invitation.LastName}
                        </Typography>
                        {!invitation.AcceptDate && (
                          <Typography variant="body2" style={{ color: '#ff9800', fontWeight: 700 }}>
                            Pending
                          </Typography>
                        )}
                      </Box>
                      
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Email:</strong> {invitation.Email}
                        </Typography>
                        <Typography style={{ color: '#666', fontSize: '0.75rem', fontWeight: 600 }}>•</Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Invited:</strong> {formatDate(invitation.Created)}
                        </Typography>
                        {invitation.IsCollectEnabled !== null && (
                          <>
                            <Typography style={{ color: '#666', fontSize: '0.75rem', fontWeight: 600 }}>•</Typography>
                            <Typography variant="body2" color="textSecondary">
                              <strong>Collect:</strong> {invitation.IsCollectEnabled ? 'Enabled' : 'Disabled'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                    
                    <Box
                      onClick={() => copyToClipboard(invitation.InvitationUrl)}
                      style={{ 
                        marginLeft: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '8px',
                        transition: 'background-color 0.2s',
                        alignSelf: 'flex-start'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FileCopyIcon style={{ fontSize: '20px', color: '#666', marginBottom: '4px' }} />
                      <Typography variant="caption" style={{ fontSize: '0.7rem', color: '#666', textAlign: 'center' }}>
                        Invitation URL
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid
              container
              item
              justifyContent="center"
              alignItems="center"
              style={{ marginTop: "96px" }}
            >
              <Grid
                container
                item
                justifyContent="center"
                alignItems="center"
                style={{ maxWidth: "320px" }}
              >
                <Typography variant="h6" component="span" align="center">
                  No pending invitations
                </Typography>
              </Grid>
            </Grid>
          )
        ) : null}
      </Box>
      
      <InviteProTeamModal
        visible={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={() => {
          // Refresh the invitations list when a new invite is sent
          getInvitations();
        }}
      />
      
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Invitation URL copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
}

const useButtonStyles = makeStyles({
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive
  }
});

export default TkProTeams;
